import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

/**
 * Anonymised social-proof wall — approved members shown ONLY as initials.
 * No names, phones or photos ever leave the server, honouring the site's
 * strict privacy contract. Provides the "the club is alive" heartbeat.
 */
export default function MembersWall() {
    const [data, setData] = useState({ total: null, members: [] });

    useEffect(() => {
        let alive = true;
        api.get("/public/members-wall")
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const members = data.members || [];
    // Ensure we always show a full grid — pad with anonymous silhouettes if needed
    const min = 24;
    const padded = members.length >= min
        ? members
        : [...members, ...Array(min - members.length).fill({ initials: "·", anon: true })];

    return (
        <section
            className="relative py-24 md:py-32 lg:py-40 border-t border-emerald-line/50 overflow-hidden"
            data-testid="members-wall"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                {/* Head */}
                <div className="grid grid-cols-12 gap-6 items-end mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-7">
                        <div className="label-eyebrow flex items-center gap-3">
                            <span className="w-8 h-px bg-gold" />
                            The Circle
                        </div>
                        <h2 className="font-serif font-medium text-4xl sm:text-5xl md:text-6xl leading-[1.03] tracking-tight mt-4 max-w-2xl">
                            Inside the velvet rope,
                            <br />
                            <span className="italic text-gold">already
                                {" "}
                                <MemberCount total={data.total} />
                                {" "}
                                strong.
                            </span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 md:text-right">
                        <p className="text-ink-secondary text-sm md:text-base leading-relaxed font-light max-w-md md:ml-auto">
                            Every member is reviewed by hand. Identity stays private —
                            you see initials, not names. The door sees the rest.
                        </p>
                    </div>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-3 md:gap-4">
                    {padded.slice(0, min).map((m, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.85 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{
                                duration: 0.55,
                                ease: [0.22, 1, 0.36, 1],
                                delay: (i % 12) * 0.03 + Math.floor(i / 12) * 0.05,
                            }}
                            className={`aspect-square rounded-full border flex items-center justify-center relative group ${
                                m.anon
                                    ? "border-white/8 bg-white/[0.015]"
                                    : m.vip
                                        ? "border-gold/60 bg-gradient-to-br from-gold/15 to-transparent"
                                        : "border-gold/25 bg-gradient-to-br from-white/[0.04] to-transparent"
                            }`}
                            data-testid={m.anon ? `member-anon-${i}` : `member-chip-${i}`}
                        >
                            <span
                                className={`font-serif italic text-sm sm:text-base md:text-lg tracking-tight ${
                                    m.anon ? "text-ink-muted" : m.vip ? "text-gold" : "text-ink-primary/85"
                                }`}
                            >
                                {m.initials}
                            </span>
                            {m.vip && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.9)]" />
                            )}
                        </motion.div>
                    ))}
                </div>

                {/* Footnote */}
                <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3 text-ink-muted text-[11px] tracking-[0.28em] uppercase">
                    <span className="inline-flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold pulse-dot" />
                        Live count
                    </span>
                    <span className="hidden sm:inline">·</span>
                    <span>Latest members shown by initials — anonymity guaranteed</span>
                </div>
            </div>
        </section>
    );
}

/* Live count with fluid number transition */
function MemberCount({ total }) {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        if (total == null) return;
        const start = performance.now();
        const from = display;
        const to = total;
        const dur = 1400;
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(from + (to - from) * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [total]);
    return <span data-testid="members-count" className="tabular-nums">{display}</span>;
}
